import { useState, useEffect, useCallback } from "react";
import { List, TrendingUp, TrendingDown, Clock, DollarSign } from "lucide-react";

import { API_BASE as API } from '../config';

export default function DealLogTab({ token }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Try MongoDB deal history first
      const r1 = await fetch(`${API}/api/eu-bot/dca/deal-history`, { headers: { Authorization: `Bearer ${token}` } });
      if (r1.ok) {
        const data = await r1.json();
        if (data.deals && data.deals.length > 0) {
          setDeals(data.deals);
          setLoading(false);
          return;
        }
      }

      // Fallback: aggregate deal_log_recent from ALL bots
      const r2 = await fetch(`${API}/api/eu-bot/dca/all-bots`, { headers: { Authorization: `Bearer ${token}` } });
      if (r2.ok) {
        const data = await r2.json();
        const allDeals = [];
        for (const bot of (data.bots || [])) {
          for (const d of (bot.deal_log_recent || [])) {
            allDeals.push({
              deal_num: d.deal || 0,
              symbol: bot.symbol || "?",
              entry_price: d.entry || d.entry_price || 0,
              exit_price: d.exit || d.exit_price || 0,
              profit: d.profit || 0,
              profit_pct: d.profit_pct || 0,
              safety_orders: d.safety_orders || 0,
              invested: d.invested || 0,
              duration_hours: d.duration_hours || 0,
              closed_at: d.closed_at || "",
            });
          }
        }
        // Sort by closed_at
        allDeals.sort((a, b) => (a.closed_at || "").localeCompare(b.closed_at || ""));
        // Re-number
        allDeals.forEach((d, i) => d.deal_num = i + 1);
        setDeals(allDeals);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, [fetchData]);

  if (loading) return <div className="text-center py-20 text-zinc-600 font-mono text-sm">Loading deal history...</div>;

  // Split active vs archived so retired-bot wins stay visible but don't
  // poison active-fleet KPIs (win rate, avg profit, durations).
  const activeDeals = deals.filter(d => !d.is_archived);
  const archivedDeals = deals.filter(d => d.is_archived);

  const totalProfit = activeDeals.reduce((s, d) => s + (d.profit || 0), 0);
  const avgProfit = activeDeals.length > 0 ? totalProfit / activeDeals.length : 0;
  const wins = activeDeals.filter(d => d.profit > 0).length;
  const losses = activeDeals.filter(d => d.profit <= 0).length;
  const avgDuration = activeDeals.length > 0 ? activeDeals.reduce((s, d) => s + (d.duration_hours || 0), 0) / activeDeals.length : 0;
  const avgSOs = activeDeals.length > 0 ? activeDeals.reduce((s, d) => s + (d.safety_orders || 0), 0) / activeDeals.length : 0;
  const archivedProfit = archivedDeals.reduce((s, d) => s + (d.profit || 0), 0);

  return (
    <div className="space-y-4" data-testid="deal-log-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Active Deals</div>
          <div className="text-xl font-mono font-bold text-white" data-testid="deal-count">{activeDeals.length}</div>
          {archivedDeals.length > 0 && (
            <div className="text-[10px] text-zinc-600 font-mono">+{archivedDeals.length} archived</div>
          )}
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Active Profit</div>
          <div className={`text-xl font-mono font-bold ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {totalProfit >= 0 ? '+' : ''}${totalProfit.toFixed(2)}
          </div>
          {archivedDeals.length > 0 && (
            <div className="text-[10px] text-zinc-600 font-mono">
              archived: {archivedProfit >= 0 ? '+' : ''}${archivedProfit.toFixed(2)}
            </div>
          )}
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Avg / Deal</div>
          <div className="text-xl font-mono font-bold text-teal-400">${avgProfit.toFixed(2)}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Win Rate</div>
          <div className="text-xl font-mono font-bold text-emerald-400">
            {deals.length > 0 ? (wins / deals.length * 100).toFixed(0) : 0}%
          </div>
          <div className="text-[10px] text-zinc-600 font-mono">{wins}W / {losses}L</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Avg Duration</div>
          <div className="text-xl font-mono font-bold text-white">{avgDuration.toFixed(1)}h</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase font-mono mb-1">Avg SOs</div>
          <div className="text-xl font-mono font-bold text-amber-400">{avgSOs.toFixed(1)}</div>
        </div>
      </div>

      {/* Deal Table */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-zinc-800">
          <List className="w-4 h-4 text-teal-400" />
          <span className="text-sm font-mono font-bold text-teal-400">Deal History</span>
          <span className="text-[10px] text-zinc-600 font-mono">{deals.length} deals</span>
        </div>
        {deals.length === 0 ? (
          <div className="text-center py-12 text-zinc-600 font-mono text-sm">
            No closed deals yet — deals will appear here as the bot completes them.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono" data-testid="deal-table">
              <thead>
                <tr className="text-zinc-500 uppercase border-b border-zinc-800/50">
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Symbol</th>
                  <th className="px-4 py-2 text-right">Profit</th>
                  <th className="px-4 py-2 text-right">%</th>
                  <th className="px-4 py-2 text-right">SOs</th>
                  <th className="px-4 py-2 text-right">Invested</th>
                  <th className="px-4 py-2 text-right">Duration</th>
                  <th className="px-4 py-2 text-right">Entry</th>
                  <th className="px-4 py-2 text-right">Exit</th>
                  <th className="px-4 py-2 text-right">Closed</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors ${deal.is_archived ? 'opacity-60' : ''}`}
                    data-archived={deal.is_archived ? 'true' : 'false'}
                  >
                    <td className="px-4 py-2.5 text-zinc-400">#{deal.deal_num}</td>
                    <td className="px-4 py-2.5 text-zinc-300">
                      <span className="inline-flex items-center gap-2">
                        {deal.symbol}
                        {deal.is_archived && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-zinc-800 text-zinc-500 border border-zinc-700"
                            title={`Archived bot: ${deal.bot_id || 'unknown'} — kept for history, not counted in active fleet`}
                          >
                            ARCHIVED
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 text-right font-bold ${deal.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {deal.profit >= 0 ? '+' : ''}${deal.profit?.toFixed(2)}
                    </td>
                    <td className={`px-4 py-2.5 text-right ${deal.profit_pct >= 0 ? 'text-emerald-400/70' : 'text-red-400/70'}`}>
                      {deal.profit_pct >= 0 ? '+' : ''}{deal.profit_pct?.toFixed(2)}%
                    </td>
                    <td className={`px-4 py-2.5 text-right ${deal.safety_orders > 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                      {deal.safety_orders || 0}
                    </td>
                    <td className="px-4 py-2.5 text-right text-zinc-400">${deal.invested?.toFixed(2) || '0'}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-500">
                      {deal.duration_hours ? `${deal.duration_hours.toFixed(1)}h` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-zinc-500">${deal.entry_price?.toFixed(4) || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-500">${deal.exit_price?.toFixed(4) || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-600">
                      {deal.closed_at ? new Date(deal.closed_at).toLocaleString("el-GR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
